import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Divider
  
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import TextField from '../../components/common/TextField/TextField';
import { signInUser, createUser, signInWithGoogle, verifyUser, forgotUserPassword, confirmUserPasswordReset, getCurrentAuthUser } from '../../utils/auth';
import { trackEvent, trackPageView } from '../../utils/analytics';

const AuthContainer = styled(Box)`
  min-height: calc(100vh - 160px);
  width: 100%;
  padding: 120px 0;
  margin-top: 60px;
  background: linear-gradient(108deg, #010d09 2.43%, #122051 69.6%, #26285f 98.68%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AuthCard = styled(Card)`
  width: 100%;
  max-width: 440px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`;

const GoogleButton = styled(Button)`
  width: 100%;
  padding: 12px;
  margin-bottom: 24px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background-color: #fff;
  color: #000;
  font-weight: 500;
  text-transform: none;
  
  &:hover {
    background-color: #f8f8f8;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const OrDivider = styled(Box)`
  display: flex;
  align-items: center;
  margin: 24px 0;
  
  hr {
    flex: 1;
  }
  
  span {
    margin: 0 16px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const ActionLink = styled('span')`
  color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
  font-size: 0.875rem;
  margin-left: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  padding: 12px;
  margin-top: 24px;
  background-color: #000;
  color: #fff;
  font-weight: 600;
  
  &:hover {
    background-color: #000;
    opacity: 0.9;
  }
`;

interface AuthProps {
  onAuthSuccess?: (email: string) => void;
}

const Auth: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignIn, setIsSignIn] = useState(true);
  const [verify, setVerify] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const returnPath = (location.state as { from?: string })?.from || '/wallets';
    // Track auth page view in funnel
    trackPageView('/auth', {
      funnel_stage: 'auth_page',
      auth_type: isSignIn ? 'sign_in' : 'sign_up',
      has_return_path: !!location.state
    });
  }, [isSignIn, location.state]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const handleAuthSuccess = () => {
    const from = (location.state as { from?: Location })?.from?.pathname || '/wallets';
    console.log('Auth success, navigating to:', from);
    trackEvent('auth_success', {
      method: 'email',
      redirect_to: from
    });
    window.location.href = from;
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      console.log('Starting Google sign-in flow...');
      await signInWithGoogle();
      console.log('Google sign-in completed, fetching user...');
      const user = await getCurrentAuthUser();
      console.log('Google sign-in - Current user:', user);
      if (user && onAuthSuccess && user.username) {
        console.log('Google sign-in - Setting user email:', user.username);
        await onAuthSuccess(user.username);
        trackEvent('login', {
          method: 'google',
          success: true
        });
        // Force a full page reload
        const from = (location.state as { from?: Location })?.from?.pathname || '/wallets';
        console.log('Google sign-in success, redirecting to:', from);
        window.location.href = from;
      }
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      setError(error.message);
      trackEvent('login', {
        method: 'google',
        success: false,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error: any): string => {
    const errorMessage = error.message || '';
    
    if (errorMessage.includes('UsernameExistsException')) {
      return 'An account with this email already exists. Please sign in or use a different email.';
    }
    if (errorMessage.includes('UserNotFoundException')) {
      return 'No account found with this email. Please check your email or sign up.';
    }
    if (errorMessage.includes('NotAuthorizedException')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (errorMessage.includes('CodeMismatchException')) {
      return 'Invalid verification code. Please check and try again.';
    }
    if (errorMessage.includes('ExpiredCodeException')) {
      return 'Verification code has expired. Please request a new one.';
    }
    if (errorMessage.includes('LimitExceededException')) {
      return 'Too many attempts. Please try again later.';
    }
    if (errorMessage.includes('InvalidPasswordException')) {
      return 'Password must contain:\n• Minimum 8 characters\n• At least 1 number\n• At least 1 special character\n• At least 1 uppercase letter\n• At least 1 lowercase letter';
    }
    
    return error.message || 'An error occurred. Please try again.';
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least 1 number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least 1 special character';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least 1 uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least 1 lowercase letter';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (forgotPassword) {
        await forgotUserPassword(formData.email);
        trackEvent('funnel_step', {
          step_name: 'password_reset_requested',
          success: true
        });
        setForgotPassword(false);
        setResetPassword(true);
      } else if (resetPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        
        const passwordError = validatePassword(formData.newPassword);
        if (passwordError) {
          throw new Error(passwordError);
        }
        
        await confirmUserPasswordReset(formData.email, formData.code, formData.newPassword);
        trackEvent('funnel_step', {
          step_name: 'password_reset_complete',
          success: true
        });
        setResetPassword(false);
        setIsSignIn(true);
        setFormData({
          email: '',
          password: '',
          code: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else if (verify) {
        await verifyUser(formData.email, formData.code);
        trackEvent('funnel_step', {
          step_name: 'email_verification_complete',
          success: true
        });
        setVerify(false);
        setIsSignIn(true);
      } else if (isSignIn) {
        await signInUser(formData.email, formData.password);
        const user = await getCurrentAuthUser();
        
        if (user?.username) {
          trackEvent('funnel_step', {
            step_name: 'auth_success',
            auth_type: 'sign_in',
            has_return_path: !!location.state
          });
          
          if (onAuthSuccess) {
            await onAuthSuccess(user.username);
          }

          // If returning from wallet creation, track the return
          if (location.state) {
            trackEvent('funnel_step', {
              step_name: 'return_to_wallet_creation',
              auth_type: 'sign_in'
            });
          }

          const from = (location.state as { from?: Location })?.from?.pathname || '/wallets';
          console.log('Auth success, navigating to:', from);
          window.location.href = from;
        }
      } else {
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
          throw new Error(passwordError);
        }
        
        await createUser(formData.email, formData.password);
        trackEvent('funnel_step', {
          step_name: 'auth_success',
          auth_type: 'sign_up',
          has_return_path: !!location.state
        });
        setVerify(true);
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      trackEvent('funnel_step', {
        step_name: 'auth_error',
        auth_type: isSignIn ? 'sign_in' : 'sign_up',
        error_type: error.message,
        has_return_path: !!location.state
      });
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleForm = () => {
    setIsSignIn(!isSignIn);
    setError(null);
  };

  return (
    <AuthContainer>
      <Container maxWidth="sm">
        <AuthCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 3 }}>
              {isSignIn && !forgotPassword && !resetPassword ? 'Sign In' : 
               verify ? 'Verify Email' : 
               forgotPassword ? 'Reset Password' : 
               resetPassword ? 'Enter Code' : 'Create Account'}
            </Typography>

            {!verify && !forgotPassword && !resetPassword && (
              <GoogleButton
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#FFC107"/>
                  <path d="M3.15295 7.3455L6.43845 9.755C7.32745 7.554 9.48045 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15895 2 4.82795 4.1685 3.15295 7.3455Z" fill="#FF3D00"/>
                  <path d="M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5718 17.5742 13.3038 18.001 12 18C9.39903 18 7.19053 16.3415 6.35853 14.027L3.09753 16.5395C4.75253 19.778 8.11353 22 12 22Z" fill="#4CAF50"/>
                  <path d="M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.785L18.7045 19.404C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#1976D2"/>
                </svg>
                Continue with Google
              </GoogleButton>
            )}

            {!verify && !forgotPassword && !resetPassword && (
              <OrDivider>
                <Divider />
                <span>or</span>
                <Divider />
              </OrDivider>
            )}

            <form onSubmit={handleSubmit}>
              {error && (
                <Typography color="error" sx={{ mb: 2 }} align="center">
                  {error}
                </Typography>
              )}

              {forgotPassword && !resetPassword && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Enter your email address and we'll send you a verification code to reset your password.
                  </Typography>
                  <TextField
                    name="email"
                    label="Email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    error={!!error}
                  />
                </>
              )}

              {resetPassword && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Enter the verification code sent to {formData.email}
                  </Typography>
                  <TextField
                    name="code"
                    label="Verification Code"
                    placeholder="Enter verification code"
                    value={formData.code}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    error={!!error}
                  />
                  <TextField
                    name="newPassword"
                    label="New Password"
                    placeholder="Enter new password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    error={!!error}
                    helperText="Password must be at least 8 characters long"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder="Confirm new password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    error={!!error}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}

              {!forgotPassword && !resetPassword && !verify && (
                <>
                  <TextField
                    name="email"
                    label="Email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    disabled={verify || resetPassword}
                    required
                  />

                  <TextField
                    name="password"
                    label="Password"
                    placeholder="Enter your password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}

              {verify && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Enter the verification code sent to {formData.email}
                  </Typography>
                  <TextField
                    name="email"
                    label="Email"
                    value={formData.email}
                    disabled
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    name="code"
                    label="Verification Code"
                    placeholder="Enter verification code"
                    value={formData.code}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                  />
                </>
              )}

              {isSignIn && !verify && !forgotPassword && !resetPassword && (
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <ActionLink onClick={() => {
                    setForgotPassword(true);
                    setFormData(prev => ({
                      ...prev,
                      password: '',
                      code: '',
                      newPassword: '',
                      confirmPassword: ''
                    }));
                  }}>
                    Forgot Password?
                  </ActionLink>
                </Box>
              )}

              <SubmitButton
                type="submit"
                variant="contained"
                disabled={loading || (forgotPassword && !formData.email)}
              >
                {isSignIn && !forgotPassword && !resetPassword ? 'Sign In' : 
                 verify ? 'Verify' : 
                 forgotPassword ? 'Send Reset Code' : 
                 resetPassword ? 'Reset Password' : 'Sign Up'}
              </SubmitButton>
            </form>

            {!verify && !forgotPassword && !resetPassword && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {isSignIn ? "Don't have an account? " : "Already have an account? "}
                  <ActionLink onClick={handleToggleForm}>
                    {isSignIn ? 'Sign Up' : 'Sign In'}
                  </ActionLink>
                </Typography>
              </Box>
            )}

            {(verify || forgotPassword || resetPassword) && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  <ActionLink
                    onClick={() => {
                      setVerify(false);
                      setForgotPassword(false);
                      setResetPassword(false);
                      setIsSignIn(true);
                      setFormData({
                        email: '',
                        password: '',
                        code: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                    }}
                  >
                    Back to Sign In
                  </ActionLink>
                </Typography>
              </Box>
            )}
          </CardContent>
        </AuthCard>
      </Container>
    </AuthContainer>
  );
};

export default Auth; 